import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Clock, LoaderCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  ROLE_OPTIONS,
  SITES_OPTIONS,
  STEPS,
  emptyAnswers,
  toPayload,
  validateStep,
  venueTypeFromParam,
  type Answers,
  type Photo,
  type StepId,
  type VenueType,
} from '@/onboarding/config';
import {
  AgreementStep,
  BusyStep,
  ContactStep,
  ProductsStep,
  SpotStep,
  VenueTypeStep,
  WhereStep,
} from '@/onboarding/steps';

const DRAFT_KEY = 'bb-venue-onboarding-draft-v1';

const STEP_COPY: Record<StepId, { title: string; subtitle?: string }> = {
  type: { title: 'Select Venue Type' },
  where: { title: 'About your venue' },
  busy: { title: 'Members & Footfall' },
  spot: { title: 'Location setup' },
  products: { title: 'Products & Advertising' },
  agreement: { title: 'Venue Requirements', subtitle: 'A few details to help us confirm we can move forward.' },
  contact: { title: 'Your details' },
};

type StepCopy = { title: string; subtitle?: string };

const TYPE_STEP_COPY: Partial<Record<VenueType, Partial<Record<StepId, StepCopy>>>> = {
  campus: {
    busy: { title: 'Students & Footfall', subtitle: 'Help us understand the size and traffic of your campus.' },
    products: { title: 'Products & Advertising', subtitle: "Help us understand what's allowed on your campus." },
  },
  transit: {
    busy: { title: 'Passengers & Footfall', subtitle: 'Help us understand the size and traffic of your network.' },
  },
  retail: {
    busy: { title: 'Shoppers & Footfall', subtitle: 'Help us understand the traffic at your locations.' },
  },
  other: {
    busy: { title: 'Visitors & Footfall', subtitle: 'Help us understand the traffic at your locations.' },
  },
};

interface Draft {
  answers: Answers;
  step: StepId;
  photos: Photo[];
}

function readDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<Draft>;
    const answers: Answers = { ...emptyAnswers, ...draft.answers };
    if (answers.sitesCount && !SITES_OPTIONS.includes(answers.sitesCount)) answers.sitesCount = undefined;
    if (answers.contactRole && !ROLE_OPTIONS.includes(answers.contactRole)) answers.contactRole = undefined;
    if (answers.securitySide && !['landside', 'airside', 'both'].includes(answers.securitySide)) answers.securitySide = undefined;
    return {
      answers,
      step:
        (draft.step as string) === 'screen'
          ? 'products'
          : STEPS.includes(draft.step as StepId)
            ? (draft.step as StepId)
            : 'type',
      photos: Array.isArray(draft.photos) ? draft.photos : [],
    };
  } catch {
    return null;
  }
}

function writeDraft(draft: Draft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Photos can exceed the storage quota; keep the answers at least.
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, photos: [] }));
    } catch {
      // Storage unavailable (private mode). The form still works without a draft.
    }
  }
}

interface Result {
  monthlyFootfall: number;
}

export default function Onboarding() {
  const [params] = useSearchParams();
  const urlType = React.useMemo(() => venueTypeFromParam(params.get('type')), [params]);
  const steps = React.useMemo<StepId[]>(() => (urlType ? STEPS.filter((s) => s !== 'type') : [...STEPS]), [urlType]);

  const [initial] = React.useState(() => {
    const draft = readDraft();
    const answers = { ...(draft?.answers ?? emptyAnswers), ...(urlType ? { venueType: urlType } : {}) };
    const step = draft && steps.includes(draft.step) ? draft.step : steps[0];
    return { answers, step, photos: draft?.photos ?? [] };
  });

  const [answers, setAnswers] = React.useState<Answers>(initial.answers);
  const [photos, setPhotos] = React.useState<Photo[]>(initial.photos);
  const [step, setStep] = React.useState<StepId>(initial.step);
  const [showErrors, setShowErrors] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [result, setResult] = React.useState<Result | null>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!result) writeDraft({ answers, step, photos });
  }, [answers, step, photos, result]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, result]);

  const index = steps.indexOf(step);
  const isLast = index === steps.length - 1;
  const errors = showErrors ? validateStep(step, answers) : {};
  const set = React.useCallback((patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch })), []);

  const goTo = (next: StepId) => {
    setShowErrors(false);
    setSubmitError('');
    setStep(next);
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch('/api/onboarding/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toPayload(answers, photos)),
      });
      if (!res.ok) {
        setSubmitError(
          res.status === 400
            ? 'Some answers need another look. Go back through the screens and check them.'
            : res.status === 429
              ? 'Too many tries. Please wait a few minutes and try again.'
              : 'We could not send your answers. Please try again.',
        );
        return;
      }
      const body = (await res.json()) as Result;
      localStorage.removeItem(DRAFT_KEY);
      setResult(body);
    } catch {
      setSubmitError('We could not reach our server. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onContinue = () => {
    if (Object.keys(validateStep(step, answers)).length > 0) {
      setShowErrors(true);
      window.requestAnimationFrame(() => {
        cardRef.current?.querySelector('[data-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    if (isLast) void submit();
    else goTo(steps[index + 1]);
  };

  const onPickType = (venueType: VenueType) => set({ venueType });

  const showIntro = index === 0 && !result;
  const stepNumber = steps[0] === 'type' ? index : index + 1;
  const stepTotal = STEPS.length - 1;
  const progress = result ? 100 : Math.max((stepNumber / stepTotal) * 100, 5);
  const copy = (answers.venueType && TYPE_STEP_COPY[answers.venueType]?.[step]) || STEP_COPY[step];

  return (
    <div className="onboarding min-h-screen bg-[#F4F6FA] text-[#0A1628] antialiased">
      <header className="mx-auto w-full max-w-xl px-4 pt-6 sm:px-5 lg:max-w-3xl lg:px-9 lg:pt-8">
        <div className="flex items-center justify-between gap-3">
          <img src="/brand/bb-logo-dark.svg" alt="bright.blue" className="h-8 w-auto" />
          <span
            aria-hidden={!showIntro}
            className={cn(
              'flex items-center gap-1.5 text-sm text-slate-500 transition-opacity duration-500 motion-reduce:transition-none',
              showIntro ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
            Takes about 60 seconds
          </span>
        </div>
        <div
          aria-hidden={!showIntro}
          className={cn(
            'grid transition-[grid-template-rows,opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
            showIntro ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] -translate-y-2 opacity-0',
          )}
        >
          <div className="overflow-hidden">
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-[#0A1628]">Venue Application</h1>
            <p className="mt-1.5 text-base text-slate-500">Apply to host a bright.blue location.</p>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-xl px-3 pb-10 pt-5 sm:px-4 lg:max-w-3xl lg:px-8 lg:pb-8">
        <div className="mb-4 flex items-center gap-4 px-1">
          <Progress
            value={progress}
            aria-label="Progress"
            className="h-2.5 flex-1 bg-slate-200 [&>div]:rounded-full [&>div]:bg-[#0A5CFF]"
          />
          {!result && (
            <p className="shrink-0 text-sm text-slate-500">
              Step {stepNumber} of {stepTotal}
            </p>
          )}
        </div>

        <Card ref={cardRef} className="rounded-3xl border-0 bg-white shadow-[0_1px_3px_rgba(10,22,40,0.06),0_8px_24px_rgba(10,22,40,0.06)]">
          {result ? (
            <DoneScreen />
          ) : (
            <>
              <div className="space-y-1 px-5 pb-2 pt-6 sm:px-7 lg:px-10 lg:pt-8">
                <h2 className="text-3xl font-bold tracking-tight">{copy.title}</h2>
                {copy.subtitle && <p className="text-sm text-slate-500">{copy.subtitle}</p>}
              </div>

              <div className="space-y-7 px-5 py-5 sm:px-7 lg:px-10 lg:py-5">
                {step === 'type' && <VenueTypeStep a={answers} onPick={onPickType} />}
                {step === 'where' && <WhereStep a={answers} set={set} errors={errors} />}
                {step === 'busy' && <BusyStep a={answers} set={set} errors={errors} />}
                {step === 'spot' && <SpotStep a={answers} set={set} errors={errors} photos={photos} setPhotos={setPhotos} />}
                {step === 'products' && <ProductsStep a={answers} set={set} errors={errors} />}
                {step === 'agreement' && <AgreementStep a={answers} set={set} errors={errors} />}
                {step === 'contact' && <ContactStep a={answers} set={set} errors={errors} />}
                {errors.venueType && <p className="text-sm font-medium text-red-600">{errors.venueType}</p>}
              </div>

              {submitError && (
                <p role="alert" className="mx-5 mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700 sm:mx-7 lg:mx-10">
                  {submitError}
                </p>
              )}

              <div className="sticky bottom-0 flex items-center gap-3 rounded-b-3xl border-t border-slate-100 bg-white/95 px-5 py-4 backdrop-blur sm:px-7 lg:px-10">
                {index > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => goTo(steps[index - 1])}
                    disabled={submitting}
                    className="h-12 rounded-full bg-slate-100 px-6 text-base font-semibold text-[#0A1628] hover:bg-slate-200"
                  >
                    <ArrowLeft />
                    Back
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={onContinue}
                  disabled={submitting}
                  className="ml-auto h-12 flex-1 rounded-full bg-[#0A5CFF] text-base font-bold text-white shadow-md hover:bg-[#0A5CFF]/90 sm:flex-none sm:px-10"
                >
                  {submitting ? (
                    <>
                      <LoaderCircle className="animate-spin" />
                      Sending
                    </>
                  ) : isLast ? (
                    <>
                      Submit application
                      <ArrowRight />
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight />
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}

function DoneScreen() {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <span className="grid h-16 w-16 place-content-center rounded-full bg-[#0A5CFF] text-white">
        <Check className="h-8 w-8" strokeWidth={3} />
      </span>
      <h1 className="mt-5 text-2xl font-semibold tracking-tight">Thanks for submitting.</h1>
      <p className="mt-2 max-w-sm text-slate-500">
        Our team will review your responses and reply within 2 business days to confirm next steps.
      </p>
      <p className="mt-3 max-w-sm text-sm font-medium text-slate-600">Check your email for updates.</p>    </div>
  );
}
